import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { productVerticalMasterAPI, masterDataAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditBusinessVerticalMaster.css";
import { getRoutePermissions } from "../utils/auth";

const extractMarketsList = (marketRes) => {
  if (!marketRes) return [];
  if (marketRes?.success && marketRes?.data) {
    const payload = marketRes.data;
    if (Array.isArray(payload.markets)) return payload.markets;
    if (Array.isArray(payload.Markets)) return payload.Markets;
  }
  if (Array.isArray(marketRes?.data?.Markets)) return marketRes.data.Markets;
  if (Array.isArray(marketRes?.data?.markets)) return marketRes.data.markets;
  if (Array.isArray(marketRes?.data)) return marketRes.data;
  if (Array.isArray(marketRes?.Markets)) return marketRes.Markets;
  if (Array.isArray(marketRes?.markets)) return marketRes.markets;
  return [];
};

const normalizeMarket = (item) => ({
  marketCode: item.marketCode || item.MarketCode || item.marketcode || "",
  marketName: item.marketName || item.MarketName || item.marketname || "",
});

const normalizeProductVertical = (item) => ({
  bvCode: item.bvCode || item.BVCode || item.BVCODE || item.catgCode || item.CatgCode || "",
  bvId: item.bvid || item.bvId || item.bVID || item.BVID || item.catgId || item.CatgId || "",
  bvName: item.bvName || item.BVName || item.BVNAME || item.catgName || item.CatgName || "",
  marketCode: item.marketCode || item.MarketCode || item.MARKETCODE || "",
  marketName: item.marketName || item.MarketName || item.MARKETNAME || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const AddEditBusinessVerticalMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bvCode } = useParams();
  const isViewMode = location.pathname.includes("/view/");
  const isEditMode = !!bvCode && !isViewMode;

  const routePermissions = getRoutePermissions("/product-verticals");
  const { canAdd, canEdit, canView } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [productVertical, setProductVertical] = useState({
    bvCode: "",
    bvId: "",
    bvName: "",
    marketCode: "",
    marketName: "",
    isActive: true,
  });

  const [dropdowns, setDropdowns] = useState({ markets: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const marketSelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (isViewMode && !canView) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to view business verticals.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/product-verticals"));
      return;
    }
    if (!isViewMode && !canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isEditMode ? "edit" : "add"} business verticals.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/product-verticals"));
      return;
    }
    loadDropdowns();
    if (isEditMode || isViewMode) {
      loadBusinessVertical();
    } else {
      loadNextBusinessVerticalCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bvCode, canSave, canView, isViewMode]);

  useEffect(() => {
    if (dropdowns.markets.length === 0 || isViewMode) return;
    if (!marketSelectRef.current) return;

    const $marketSelect = $(marketSelectRef.current);
    if ($marketSelect.data("select2")) {
      $marketSelect.select2("destroy");
    }
    $marketSelect.select2({
      width: "100%",
      placeholder: "Select Market",
      allowClear: false,
    });
    $marketSelect.on("select2:select select2:clear change", function () {
      const selectedMarketCode = $(this).val() || "";
      handleChange("marketCode", selectedMarketCode);
    });

    if (productVertical.marketCode) {
      $marketSelect.val(productVertical.marketCode).trigger("change.select2");
    }

    return () => {
      if ($marketSelect.data("select2")) {
        $marketSelect.select2("destroy");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.markets, isViewMode, productVertical.marketCode]);

  useEffect(() => {
    if (isViewMode || !marketSelectRef.current || !$(marketSelectRef.current).data("select2")) return;
    const $marketSelect = $(marketSelectRef.current);
    if (($marketSelect.val() || "") !== (productVertical.marketCode || "")) {
      $marketSelect.val(productVertical.marketCode || "").trigger("change.select2");
    }
  }, [productVertical.marketCode, isViewMode]);

  const loadDropdowns = async () => {
    try {
      const marketRes = await masterDataAPI.getMarkets("", 1, 500);
      const markets = extractMarketsList(marketRes)
        .map(normalizeMarket)
        .filter((m) => m.marketCode);
      setDropdowns({ markets });
    } catch (e) {
      console.error("Error loading markets:", e);
      setError("Failed to load market list. Please refresh the page.");
    }
  };

  const loadNextBusinessVerticalCode = async () => {
    try {
      const response = await productVerticalMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setProductVertical((prev) => ({ ...prev, bvCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next business vertical code:", e);
      setProductVertical((prev) => ({ ...prev, bvCode: "CATG00001" }));
    }
  };

  const loadBusinessVertical = async () => {
    try {
      setLoading(true);
      const response = await productVerticalMasterAPI.getByCode(bvCode);
      if (response && response.success && response.data) {
        setProductVertical(normalizeProductVertical(response.data));
      } else {
        navigate("/product-verticals");
      }
    } catch (e) {
      console.error("Error loading business vertical:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load business vertical details", "error");
      }
      navigate("/product-verticals");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProductVertical((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!productVertical.bvCode?.trim()) {
      Swal.fire("Error", "Business Vertical Code is required", "error");
      return false;
    }
    if (!productVertical.bvId?.trim()) {
      Swal.fire("Error", "Business Vertical ID is required", "error");
      return false;
    }
    if (!productVertical.bvName?.trim()) {
      Swal.fire("Error", "Business Vertical Name is required", "error");
      return false;
    }
    if (!productVertical.marketCode?.trim()) {
      Swal.fire("Error", "Market is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      const payload = {
        bvCode: productVertical.bvCode,
        bvId: productVertical.bvId,
        bvName: productVertical.bvName,
        marketCode: productVertical.marketCode,
        isActive: productVertical.isActive,
      };

      if (isEditMode) {
        response = await productVerticalMasterAPI.update(bvCode, payload, createdBy);
      } else {
        response = await productVerticalMasterAPI.create(payload, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Business Vertical saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/product-verticals");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save business vertical",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving business vertical:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save business vertical. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const marketDisplayName =
    productVertical.marketName ||
    dropdowns.markets.find((m) => m.marketCode === productVertical.marketCode)?.marketName ||
    productVertical.marketCode ||
    "-";

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/product-verticals"
            onClick={(e) => {
              e.preventDefault();
              navigate("/product-verticals");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Business Vertical Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isViewMode
              ? "View Business Vertical"
              : isEditMode
                ? "Edit Business Vertical"
                : "Add Business Vertical"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/product-verticals")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4" style={{ display: "none" }}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Business Vertical Code <span className="required-asterisk">*</span>
                </label>
                <TextField
                  value={productVertical.bvCode}
                  required
                  fullWidth
                  disabled
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Business Vertical ID <span className="required-asterisk">*</span>
                </label>
                <TextField
                  value={productVertical.bvId}
                  onChange={(e) => handleChange("bvId", e.target.value)}
                  required
                  fullWidth
                  disabled={isViewMode}
                  size="small"
                  placeholder="Enter business vertical ID"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              {isViewMode ? (
                <div className="textfield-wrapper">
                  <label className="select2-label pnlLblsze">Market</label>
                  <TextField
                    value={marketDisplayName || "-"}
                    fullWidth
                    disabled
                    size="small"
                    className="add-edit-product-textfield"
                  />
                </div>
              ) : (
                <div className="select2-wrapper">
                  <label className="select2-label pnlLblsze">
                    Market <span className="required-asterisk">*</span>
                  </label>
                  <select
                    ref={marketSelectRef}
                    className="form-control select2-category"
                    defaultValue={productVertical.marketCode || ""}
                    required
                  >
                    <option value="">Select Market</option>
                    {dropdowns.markets.map((m) => (
                      <option key={m.marketCode} value={m.marketCode}>
                        {m.marketName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Business Vertical Name <span className="required-asterisk">*</span>
                </label>
                <TextField
                  value={productVertical.bvName}
                  onChange={(e) => handleChange("bvName", e.target.value)}
                  required
                  fullWidth
                  disabled={isViewMode}
                  size="small"
                  placeholder="Enter business vertical name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            {isViewMode && (
              <div className="col-12 col-md-6 col-lg-4">
                <div className="textfield-wrapper">
                  <label className="select2-label pnlLblsze">Status</label>
                  <TextField
                    value={productVertical.isActive ? "Active" : "Inactive"}
                    fullWidth
                    disabled
                    size="small"
                    className="add-edit-product-textfield"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/product-verticals")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                {isViewMode ? "Close" : "Cancel"}
              </Button>
              {!isViewMode && (
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !canSave}
                  startIcon={
                    saving ? <CircularProgress size={20} /> : <SaveIcon />
                  }
                  className="btn-save-product funbtns"
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              )}
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditBusinessVerticalMaster;
